import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { companyLookupAPI } from '../services/api';
import { showMessage } from '../utils/messageUtils';
import Pagination from './Pagination';

const DEFAULT_PER_PAGE = 3;

// Get API docs URL - same logic as api.js getBaseURL
const getApiDocsUrl = () => {
  const envValue = process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL.trim() : '';
  const hasWindow = typeof window !== 'undefined';
  const hostname = hasWindow ? window.location.hostname : '';
  const protocol = hasWindow ? window.location.protocol : '';
  const isLocalHost =
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname === '' ||
    hostname.endsWith('.local');

  // Priority 1: Use explicit env var if provided
  if (envValue !== '') {
    const envIsAbsoluteUrl = envValue.startsWith('http://') || envValue.startsWith('https://');
    const envIsLocalUrl =
      envValue.includes('localhost') ||
      envValue.includes('127.0.0.1') ||
      envValue.includes('://0.0.0.0');

    // If the env value points to localhost but we're not on localhost, use relative URL
    if (hasWindow && !isLocalHost && envIsLocalUrl) {
      return '/api/v2/docs';
    }

    if (envValue.startsWith('/')) {
      const normalized = envValue.replace(/\/+$/, '');
      if (normalized === '' || normalized === '/api') {
        return '/api/v2/docs';
      }
      return `${normalized}/api/v2/docs`;
    }

    if (envIsAbsoluteUrl) {
      return `${envValue}/api/v2/docs`;
    }

    return `/${envValue.replace(/^\/+/, '')}/api/v2/docs`;
  }

  // Priority 2: Runtime detection - for production domains use relative URL
  if (hasWindow && !isLocalHost) {
    return '/api/v2/docs';
  }

  // Priority 3: If running on localhost but via HTTPS, use relative path
  if (hasWindow && isLocalHost && protocol === 'https:') {
    return '/api/v2/docs';
  }

  // Priority 4: NODE_ENV hints production
  if (process.env.NODE_ENV === 'production') {
    return '/api/v2/docs';
  }

  // Development fallback - explicit localhost for local dev scenarios
  return 'http://localhost:50102/api/v2/docs';
};

const dedupeValues = (values = []) => {
  const seen = new Set();
  const result = [];
  values.forEach((value) => {
    const text = String(value || '').trim();
    if (!text) return;
    const key = text.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    result.push(text);
  });
  return result;
};

const legacyAssetPatternToRegexList = (assetPattern = {}) => {
  const patterns = [];
  if (assetPattern.global_asset_id) {
    patterns.push(`^${String(assetPattern.global_asset_id).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}$`);
  }
  if (assetPattern.asset_id_starts_with) {
    patterns.push(`^${String(assetPattern.asset_id_starts_with).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`);
  }
  if (assetPattern.asset_id_contains) {
    patterns.push(String(assetPattern.asset_id_contains).replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  }
  if (assetPattern.asset_id_ends_with) {
    patterns.push(`${String(assetPattern.asset_id_ends_with).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}$`);
  }
  return dedupeValues(patterns);
};

const getCompanyNameFromDomain = (domain) => {
  const clean = String(domain || '').replace(/^https?:\/\//, '').split('.')[0];
  if (!clean) return '';
  return clean.charAt(0).toUpperCase() + clean.slice(1).replace(/-/g, ' ');
};

const mapCompanyForUi = (provider = {}) => {
  const domainRaw = provider.domain || '';
  const domain = domainRaw ? (domainRaw.includes('://') ? domainRaw : `https://${domainRaw}`) : '';
  const legacyAssetRegex = legacyAssetPatternToRegexList(provider.asset_id_pattern || {});
  const assetIdRegexPatterns = dedupeValues([
    ...(Array.isArray(provider.assetIdRegexPatterns) ? provider.assetIdRegexPatterns : []),
    ...legacyAssetRegex
  ]);

  return {
    domain,
    company_name: provider.name || provider.company_name || getCompanyNameFromDomain(domainRaw),
    company_description: provider.company_description || '',
    endpoints: provider.endpoints || [],
    assetIdRegexPatterns,
    industries: provider.industries || []
  };
};

const isLikelyDomainQuery = (query) => /^[a-z0-9-]+(\.[a-z0-9-]+)+$/i.test(query);

const CompanyCard = ({ company }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    endpoints: false,
    assetPatterns: false
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleEndpointClick = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const domain = company.domain?.replace(/^https?:\/\//, '').replace(/\/$/, '') || company.domain || '';
  const endpoints = company.endpoints || {};
  const endpointList = Array.isArray(endpoints)
    ? endpoints
        .map((endpoint) => {
          if (endpoint?.protocolInformation?.href && endpoint?.interface) {
            return { type: endpoint.interface, url: endpoint.protocolInformation.href };
          }
          if (endpoint?.type && endpoint?.url) {
            return { type: endpoint.type, url: endpoint.url };
          }
          return null;
        })
        .filter(Boolean)
    : Object.entries(endpoints).map(([type, url]) => ({ type, url }));
  const assetIdRegexPatterns = Array.isArray(company.assetIdRegexPatterns)
    ? company.assetIdRegexPatterns
    : [];
  const hasPatternInfo = assetIdRegexPatterns.length > 0;

  return (
    <div className="registry-item">
      <div className="registry-header">
        <div className="flex-grow-1">
          <h3 className="registry-title">
            {company.company_name || company.name || domain}
          </h3>
          <p className="registry-description">
            {company.company_description && (
              <span className="text-muted me-3">{company.company_description}</span>
            )}
            {domain && (
              <a href={`https://${domain}`} target="_blank" rel="noopener noreferrer" className="me-3">
                <i className="bi bi-globe"></i> https://{domain}
              </a>
            )}
          </p>
        </div>
      </div>
      
      <div className="registry-badges-row">
        <div className="registry-badges">
          {domain && (
            <a href={`https://${domain}`} target="_blank" rel="noopener noreferrer" className="badge badge-primary">
              <i className="bi bi-globe"></i>
              {domain}
            </a>
          )}
          
          {assetIdRegexPatterns.length > 0 && (
            <span className="badge badge-asset-id" title="Registered asset ID regex patterns">
              <i className="bi bi-hash"></i>
              Asset Regex: {assetIdRegexPatterns.length}
            </span>
          )}
          
          {company.industries && company.industries.length > 0 && (
            <span className="badge badge-idta-light">
              <i className="bi bi-briefcase"></i>
              {company.industries.slice(0, 2).join(', ')}
              {company.industries.length > 2 && ' +' + (company.industries.length - 2)}
            </span>
          )}
        </div>
        
        <div className="card-expand-toggle">
          <button 
            className="expand-button"
            onClick={() => setIsExpanded(!isExpanded)}
            aria-label={isExpanded ? "Collapse details" : "Expand details"}
          >
            <span>{isExpanded ? 'Hide Details' : 'Show Details'}</span>
            <i className={`bi bi-chevron-${isExpanded ? 'up' : 'down'}`}></i>
          </button>
        </div>
      </div>

      {isExpanded && (
        <div className="enhanced-data-section">
          {/* Infrastructure Endpoints */}
          {endpointList.length > 0 && (
            <div className="enhanced-section">
              <h4 
                className="dropdown-header"
                onClick={() => toggleSection('endpoints')}
                style={{ cursor: 'pointer' }}
              >
                <i className="bi bi-database"></i>
                INFRASTRUCTURE ENDPOINTS ({endpointList.length})
                <i className={`bi bi-chevron-${expandedSections.endpoints ? 'up' : 'down'} ms-auto`}></i>
              </h4>
              {expandedSections.endpoints && (
                <div className="dropdown-content">
                  {endpointList.map((endpoint, index) => (
                    <div key={index} className="server-item" onClick={() => handleEndpointClick(endpoint.url)}>
                      <div className="server-icon">
                        <i className="bi bi-link-45deg"></i>
                      </div>
                      <div className="server-info">
                        <div className="server-name">
                          {endpoint.type.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </div>
                        <div className="server-url">
                          {endpoint.url}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Asset ID & Serial Number Patterns */}
          {hasPatternInfo && (
            <div className="enhanced-section">
              <h4 
                className="dropdown-header"
                onClick={() => toggleSection('assetPatterns')}
                style={{ cursor: 'pointer' }}
              >
                <i className="bi bi-diagram-3"></i>
                ASSET ID REGEX PATTERNS
                <i className={`bi bi-chevron-${expandedSections.assetPatterns ? 'up' : 'down'} ms-auto`}></i>
              </h4>
              {expandedSections.assetPatterns && (
                <div className="dropdown-content">
                  <div className="pattern-group">
                    <div className="asset-patterns">
                      {assetIdRegexPatterns.map((pattern, index) => (
                        <div key={`asset-pattern-${index}`} className="pattern-item">
                          <i className="bi bi-hash text-primary"></i>
                          <span className="pattern-label">Asset ID Regex:</span>
                          <code className="pattern-value">{pattern}</code>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const CompanyLookup = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [allCompanies, setAllCompanies] = useState([]);
  const [filteredCompanies, setFilteredCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: DEFAULT_PER_PAGE,
    total: 0,
    total_pages: 0,
    has_prev: false,
    has_next: false
  });

  useEffect(() => {
    loadAllCompanies();
  }, []);

  const extractCompanyResult = (response) => {
    if (Array.isArray(response?.data?.result)) return response.data.result;
    if (Array.isArray(response?.data?.data)) return response.data.data;
    return [];
  };

  const extractNextCursor = (response) => response?.data?.paging_metadata?.cursor || null;

  const fetchAllCompanyPages = async (baseParams = {}) => {
    const limit = 200;
    let cursor = null;
    const aggregated = [];

    do {
      const params = { ...baseParams, limit };
      if (cursor) {
        params.cursor = cursor;
      }
      const response = await companyLookupAPI.lookup(params);
      aggregated.push(...extractCompanyResult(response));
      cursor = extractNextCursor(response);
    } while (cursor);

    return aggregated;
  };

  useEffect(() => {
    // Update pagination when filtered companies change
    const page = parseInt(searchParams.get('page'), 10) || 1;
    const perPage = parseInt(searchParams.get('per_page'), 10) || DEFAULT_PER_PAGE;
    
    const total = filteredCompanies.length;
    const totalPages = Math.ceil(total / perPage);
    const startIndex = (page - 1) * perPage;
    const endIndex = startIndex + perPage;
    
    setPagination({
      page,
      per_page: perPage,
      total,
      total_pages: totalPages,
      has_prev: page > 1,
      has_next: page < totalPages
    });
  }, [filteredCompanies, searchParams]);

  const loadAllCompanies = async () => {
    setLoading(true);
    try {
      const providers = await fetchAllCompanyPages();
      const companiesWithData = providers.map(mapCompanyForUi);

      setAllCompanies(companiesWithData);
      setFilteredCompanies(companiesWithData);
    } catch (error) {
      console.error('Error loading companies:', error);
      showMessage('Failed to load companies', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    const newParams = new URLSearchParams(searchParams);
    
    if (!searchQuery.trim()) {
      newParams.delete('search');
      newParams.set('page', '1');
      setSearchParams(newParams);
      setFilteredCompanies(allCompanies);
      return;
    }

    setSearchLoading(true);
    newParams.set('search', searchQuery.trim());
    newParams.set('page', '1');
    setSearchParams(newParams);
    
    const query = searchQuery.trim();

    try {
      let filtered = [];

      if (query.match(/^https?:\/\//i)) {
        const responseCompanies = await fetchAllCompanyPages({ asset_id: query });
        filtered = responseCompanies.map(mapCompanyForUi);
      } else if (isLikelyDomainQuery(query)) {
        const response = await companyLookupAPI.getByDomain(query);
        const singleResult = response?.data?.result ? [response.data.result] : [];
        filtered = singleResult.map(mapCompanyForUi);
      } else {
        const responseCompanies = await fetchAllCompanyPages({ name: query });
        filtered = responseCompanies.map(mapCompanyForUi);
      }

      if (filtered.length === 0) {
        showMessage('No companies found matching your query', 'info');
      } else {
        showMessage(`Found ${filtered.length} company/companies`, 'success');
      }

      setFilteredCompanies(filtered);
    } catch (error) {
      console.error('Search Error:', error);
      showMessage(error.response?.data?.error || error.message || 'Failed to search', 'error');
      setFilteredCompanies([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Get paginated companies
  const getPaginatedCompanies = () => {
    const page = pagination.page || 1;
    const perPage = pagination.per_page || DEFAULT_PER_PAGE;
    const startIndex = (page - 1) * perPage;
    const endIndex = startIndex + perPage;
    return filteredCompanies.slice(startIndex, endIndex);
  };

  if (loading) {
    return (
      <div className="search-section">
        <div className="loading-state text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Loading companies...</p>
        </div>
      </div>
    );
  }

  const paginatedCompanies = getPaginatedCompanies();

  return (
    <div>
      <div className="search-section">
        <div className="search-form">
          <div className="search-input">
            <input
              type="text"
              className="form-control"
              placeholder="Search companies by name or asset ID URL"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={searchLoading}
            />
          </div>
          <button
            className="btn btn-primary search-button-primary"
            onClick={handleSearch}
            disabled={searchLoading}
          >
            {searchLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2"></span>
                Searching...
              </>
            ) : (
              <>
                <i className="bi bi-search me-2"></i>
                Search
              </>
            )}
          </button>
        </div>
      </div>
      <div className="api-docs-button-container">
        <a 
          href={getApiDocsUrl()} 
          target="_blank" 
          rel="noopener noreferrer"
          className="api-docs-button"
        >
          <i className="bi bi-book me-2"></i>
          API Documentation
        </a>
      </div>

      <div className="registry-list">
        {paginatedCompanies.length === 0 ? (
          <div className="empty-state text-center py-5">
            <i className="bi bi-inbox" style={{ fontSize: '3rem', color: '#9CA3AF' }}></i>
            <p className="mt-3 text-muted">No companies found</p>
          </div>
        ) : (
          <>
            {paginatedCompanies.map((company, index) => (
              <CompanyCard key={index} company={company} />
            ))}
          </>
        )}
      </div>

      {filteredCompanies.length > 0 && (
        <Pagination pagination={pagination} searchParams={searchParams} />
      )}
    </div>
  );
};

export default CompanyLookup;
